import { useCallback, useEffect, useRef, useState } from "react";
import { useAction, useQuery } from "convex/react";
import { useNavigate } from "@tanstack/react-router";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { toast } from "sonner";
import { Download, Trash2 } from "lucide-react";
import { Button, ConfirmDeleteDialog } from "@/components/ui";
import { useApiMutation } from "@/packages/features-shared";
import { SettingsSection } from "./SettingsSection";
import { WorkspaceSettingsForm } from "./WorkspaceSettingsForm";
import { MemberManagement } from "./MemberManagement";

interface WorkspaceSettingsPageProps {
  workspace: {
    _id: Id<"workspaces">;
    name: string;
    slug: string;
    role: string;
    industry: string;
    companySize: string;
    jurisdiction: string;
  };
}

export function WorkspaceSettingsPage({
  workspace,
}: WorkspaceSettingsPageProps) {
  const isOwner = workspace.role === "owner";

  return (
    <>
      <WorkspaceSettingsForm workspace={workspace} />

      <div className="border-t border-border my-8" />

      <MemberManagement
        workspaceId={workspace._id}
        currentUserRole={workspace.role}
      />

      {isOwner && (
        <>
          <div className="border-t border-border my-8" />
          <DangerZone workspace={workspace} />
        </>
      )}
    </>
  );
}

// ── Danger Zone ────────────────────────────────────────────────────

function DangerZone({
  workspace,
}: {
  workspace: { _id: Id<"workspaces">; name: string };
}) {
  const deleteWorkspace = useAction(
    api.workspace_action_delete.deleteWorkspace,
  );
  const deleteDemoDataMutation = useApiMutation(api.workspaces.deleteDemoData, {
    name: "delete-demo-data",
    silentSuccess: true,
    silentErrors: true,
  });
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDemoConfirm, setShowDemoConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exportRequested, setExportRequested] = useState(false);

  const exportData = useQuery(
    api.workspace_export.exportWorkspaceData,
    exportRequested ? { workspaceId: workspace._id } : "skip",
  );

  // Track whether we already triggered the download for this request
  const downloadTriggeredRef = useRef(false);

  const handleExport = useCallback(() => {
    if (exportData) {
      triggerJsonDownload(exportData, `${workspace.name}-export.json`);
      return;
    }
    downloadTriggeredRef.current = false;
    setExportRequested(true);
  }, [exportData, workspace.name]);

  const exportPending = exportRequested && !exportData;

  // When export data arrives after request, trigger download
  useEffect(() => {
    if (exportRequested && exportData && !downloadTriggeredRef.current) {
      downloadTriggeredRef.current = true;
      triggerJsonDownload(exportData, `${workspace.name}-export.json`);
      toast.success("Export downloaded");
    }
  }, [exportRequested, exportData, workspace.name]);

  async function handleDelete() {
    setDeleting(true);
    try {
      await deleteWorkspace({ workspaceId: workspace._id });
      toast.success("Workspace deleted");
      navigate({ to: "/" });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to delete workspace",
      );
      setDeleting(false);
    }
  }

  async function handleDeleteDemoData() {
    try {
      const result = await deleteDemoDataMutation.mutateAsync({
        workspaceId: workspace._id,
      });
      const { deletedCounts } = result;
      const totalDeleted = Object.values(deletedCounts).reduce(
        (sum, count) => sum + count,
        0,
      );

      toast.success(
        `Demo data deleted successfully (${totalDeleted} records removed)`,
      );
      setShowDemoConfirm(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to delete demo data",
      );
    }
  }

  return (
    <SettingsSection
      title="Danger Zone"
      description="Irreversible and destructive actions"
    >
      <div className="border border-red-200 rounded-card p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[14px] font-medium text-primary">
              Export workspace data
            </p>
            <p className="text-[13px] text-secondary mt-0.5">
              Download all workspace data as JSON before deletion.
            </p>
          </div>
          <Button
            variant="ghost"
            onClick={handleExport}
            disabled={deleting || exportPending}
            loading={exportPending}
          >
            <Download className="h-4 w-4 mr-1.5" />
            Export Data
          </Button>
        </div>

        <div className="border-t border-red-100" />

        <div className="flex items-center justify-between">
          <div>
            <p className="text-[14px] font-medium text-primary">
              Delete demo data
            </p>
            <p className="text-[13px] text-secondary mt-0.5">
              Remove all demo data (audits, documents, checklists) from this
              workspace.
            </p>
          </div>
          <Button
            variant="danger"
            onClick={() => setShowDemoConfirm(true)}
            disabled={deleteDemoDataMutation.isPending || deleting}
            loading={deleteDemoDataMutation.isPending}
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            Delete Demo Data
          </Button>
        </div>

        <div className="border-t border-red-100" />

        <div className="flex items-center justify-between">
          <div>
            <p className="text-[14px] font-medium text-primary">
              Delete this workspace
            </p>
            <p className="text-[13px] text-secondary mt-0.5">
              Once deleted, all data in this workspace will be permanently
              removed.
            </p>
          </div>
          <Button
            variant="danger"
            onClick={() => setShowConfirm(true)}
            disabled={deleting}
          >
            Delete Workspace
          </Button>
        </div>
      </div>

      <ConfirmDeleteDialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleDelete}
        loading={deleting}
        title="Delete Workspace"
        description={`This will permanently delete "${workspace.name}" and all its data, including audits, documents, and members. This action cannot be undone.`}
        confirmMatchText={workspace.name}
        confirmLabel="Delete Workspace"
      />

      <ConfirmDeleteDialog
        open={showDemoConfirm}
        onClose={() => setShowDemoConfirm(false)}
        onConfirm={handleDeleteDemoData}
        loading={deleteDemoDataMutation.isPending}
        title="Delete Demo Data"
        description="This will permanently remove all demo data (audits, documents, checklists, and events) from this workspace. Your real data will not be affected. This action cannot be undone."
        confirmMatchText="DELETE DEMO"
        confirmLabel="Delete Demo Data"
      />
    </SettingsSection>
  );
}

// ── Helpers ────────────────────────────────────────────────────────

function triggerJsonDownload(data: unknown, filename: string) {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
